import { ContentState } from '@ms/yammer-content-state';
import {
  PraiseBadge,
  StorylineAnnouncementAudience,
  StorylineAnnouncementNotificationTarget,
} from '@ms/yammer-graphql';
import {
  BasePublisherEventProperties,
  PublisherCollapsedEventProperties,
  PublisherEntityType,
  PublisherOpenEventProperties,
} from '@ms/yammer-telemetry';

import { Topic } from '../topics/types';
import { Campaign, Entity, Group, LiveEvent, Message, TeamsMeeting, Thread, User } from '../types';

import { PublisherAttachmentReference, PublisherAttachments } from './attachments.types';

export * from './attachments.types';
export { PublisherEntityType as PublisherType };

export type HomePublisherDefaultGroupId = null;

export enum PublisherLevel {
  ThreadStarter,
  TopLevelReply,
  SecondLevelReply,
}

// This enum should be considered to be deprecated; do not add any further values to it without
// first checking in with the publisher v-team. Where possible, use existing type guards below, or
// existing selectors - especially those in publisherTypeSelectors - rather than comparing directly
// against these.
//
// Unfortunately this enum is mixing several concerns that should really be separate:
// Where a message is being posted to (group, storyline, PM)
// The content type of the message (regular, poll, praise, question)
// ...and also whether it's a new message or edit, and a threadstarter vs reply.
//
// This will hopefully replaced with something better at some point in future.
//
// Take care dealing with the Edit publisher type, since a single post type is used for editing
// all types of existing messages. It's often necessary to look up the original message to determine
// what the scope/target or content type should be. Many existing selectors already do this two-step
// check - first checking post type directly for new messages, followed by a separate check against
// the original message to handle the edit case.
export enum PublisherPostType {
  DirectNew = 'DirectNew', // @deprecated(reason: 'use publisher.destination instead')
  DirectReply = 'DirectReply', // @deprecated(reason: 'use publisher.isReply and/or publisher.destination instead')
  Edit = 'Edit', // @deprecated(reason: 'use publisher.isEditing instead')
  EventNewUpdate = 'EventNewUpdate',
  EventNewQuestion = 'EventNewQuestion',
  EventReply = 'EventReply',
  GroupNewUpdate = 'GroupNewUpdate',
  GroupNewPoll = 'GroupNewPoll',
  GroupNewPraise = 'GroupNewPraise',
  GroupNewQuestion = 'GroupNewQuestion',
  GroupNewArticle = 'GroupNewArticle',
  GroupReply = 'GroupReply',
  TeamsMeetingNewUpdate = 'TeamsMeetingNewUpdate',
  TeamsMeetingNewQuestion = 'TeamsMeetingNewQuestion',
  TeamsMeetingReply = 'TeamsMeetingReply',
  UserMomentNew = 'UserMomentNew',
  UserMomentReply = 'UserMomentReply',
  UserNewUpdate = 'UserNewUpdate',
  UserNewPoll = 'UserNewPoll',
  UserNewPraise = 'UserNewPraise',
  UserNewQuestion = 'UserNewQuestion',
  UserNewArticle = 'UserNewArticle',
  UserNewMedia = 'UserNewMedia',
  UserReply = 'UserReply',
  NetworkQuestionNewQuestion = 'NetworkQuestionNewQuestion',
  NetworkQuestionReply = 'NetworkQuestionReply',
}

export enum PublisherDestination {
  Direct = 'Direct',
  Event = 'Event',
  Group = 'Group',
  NetworkQuestion = 'NetworkQuestion',
  TeamsMeeting = 'TeamsMeeting',
  User = 'User',
  UserMoment = 'UserMoment',
}

export enum PublisherContentType {
  Regular = 'Regular',
  Poll = 'Poll',
  Praise = 'Praise',
  Question = 'Question',
  Article = 'Article',
}

export enum QuestionPromptState {
  Open = 'open',
  Dismissed = 'dismissed',
  Clicked = 'clicked',
}

export type PublisherTelemetry =
  | Omit<PublisherOpenEventProperties, 'type' | 'instanceId'>
  | Omit<PublisherCollapsedEventProperties, 'type' | 'instanceId'>
  | Omit<BasePublisherEventProperties, 'type' | 'instanceId'>;

interface TypeBasePublisher extends Entity {
  readonly isEditing: boolean;
  readonly isReply: boolean;
  readonly destination: PublisherDestination;
  readonly contentType: PublisherContentType;
}

interface BasePublisher extends TypeBasePublisher {
  readonly notifiedUserIds: User['id'][];
  readonly publisherInstanceId: string;
  readonly attachments: PublisherAttachments;
  readonly inlineAttachmentIds: string[];
  readonly hasAttemptedToAttachFile: boolean;
  readonly isGifPickerOpen: boolean;
  readonly isTopicBarOpen: boolean;
  readonly postType: PublisherPostType;
  readonly contentState?: ContentState;
  readonly title?: string;
  readonly isExpanded: boolean;
  readonly isAnnouncement: boolean;
  readonly changedGroupWhileFilesAttached: boolean;
  readonly changedPostOnBehalfOfUserWhileFilesAttached: boolean;
  readonly sharedThreadId?: Thread['id'];
  readonly sharedMessageId?: Message['id'];
  readonly topicNames: Topic['name'][];
  readonly topicIds: Topic['id'][];
  readonly isMandatoryNotification: boolean;
  readonly hasToggledMentionsBarToVisible?: boolean;
  readonly headerImage?: PublisherAttachmentReference;
  readonly questionPromptState?: QuestionPromptState;
  readonly campaignReferences: string[];
  readonly isAnswersDefault?: boolean;
}

export interface BaseReplyPublisher extends BasePublisher {
  readonly currentlyReplyingToId?: Message['id'];
  readonly isSecondLevelReply: boolean;
  readonly threadId: Thread['id'];
  readonly onBehalfOfSenderId?: User['id'];
  readonly isAnnouncement: false;
  readonly isTopicBarOpen: false;
}

export interface ThreadStarterPublisher extends BasePublisher {
  readonly badge: PraiseBadge;
  readonly pollOptions: string[];
  readonly praisedUserIds: User['id'][];
}

export interface EventThreadStarterPublisher extends ThreadStarterPublisher {
  readonly eventId: LiveEvent['id'];
  readonly groupId: Group['id'];
  readonly onBehalfOfSenderId?: User['id'];
  readonly postType: PublisherPostType.EventNewUpdate | PublisherPostType.EventNewQuestion;
  readonly destination: PublisherDestination.Event;
  readonly isEditing: false;
  readonly isReply: false;
}

export interface EventNewQuestionPublisher extends EventThreadStarterPublisher {
  readonly postType: PublisherPostType.EventNewQuestion;
  readonly destination: PublisherDestination.Event;
  readonly contentType: PublisherContentType.Question;
  readonly isEditing: false;
  readonly isReply: false;
}

export interface EventNewUpdatePublisher extends EventThreadStarterPublisher {
  readonly postType: PublisherPostType.EventNewUpdate;
  readonly destination: PublisherDestination.Event;
  readonly contentType: PublisherContentType.Regular;
  readonly isEditing: false;
  readonly isReply: false;
}

export interface EventReplyPublisher extends BaseReplyPublisher {
  readonly postType: PublisherPostType.EventReply;
  readonly destination: PublisherDestination.Event;
  readonly contentType: PublisherContentType.Regular;
  readonly isEditing: false;
  readonly isReply: true;
  readonly groupId: Group['id'];
}

export interface TeamsMeetingThreadStarterPublisher extends ThreadStarterPublisher {
  readonly teamsMeetingId: TeamsMeeting['id'];
  readonly postType: PublisherPostType.TeamsMeetingNewUpdate | PublisherPostType.TeamsMeetingNewQuestion;
  readonly destination: PublisherDestination.TeamsMeeting;
  readonly isEditing: false;
  readonly isReply: false;
  readonly isAnonymousMessage: boolean;
  readonly onBehalfOfSenderId?: User['id'];
}

export interface TeamsMeetingNewQuestionPublisher extends TeamsMeetingThreadStarterPublisher {
  readonly postType: PublisherPostType.TeamsMeetingNewQuestion;
  readonly destination: PublisherDestination.TeamsMeeting;
  readonly contentType: PublisherContentType.Question;
  readonly isEditing: false;
  readonly isReply: false;
}

export interface TeamsMeetingNewUpdatePublisher extends TeamsMeetingThreadStarterPublisher {
  readonly postType: PublisherPostType.TeamsMeetingNewUpdate;
  readonly destination: PublisherDestination.TeamsMeeting;
  readonly contentType: PublisherContentType.Regular;
  readonly isEditing: false;
  readonly isReply: false;
}

export interface TeamsMeetingReplyPublisher extends BaseReplyPublisher {
  readonly postType: PublisherPostType.TeamsMeetingReply;
  readonly destination: PublisherDestination.TeamsMeeting;
  readonly contentType: PublisherContentType.Regular;
  readonly isEditing: false;
  readonly isReply: true;
  readonly isBestReply: boolean;
}

export interface GroupThreadStarterPublisher extends ThreadStarterPublisher {
  readonly groupId: Group['id'] | HomePublisherDefaultGroupId;
  readonly campaignId?: Campaign['id'];
  readonly onBehalfOfSenderId?: User['id'];
  readonly postType:
    | PublisherPostType.GroupNewUpdate
    | PublisherPostType.GroupNewPoll
    | PublisherPostType.GroupNewPraise
    | PublisherPostType.GroupNewQuestion
    | PublisherPostType.GroupNewArticle;
  readonly destination: PublisherDestination.Group;
  readonly isEditing: false;
  readonly isReply: false;
  readonly topicId?: Topic['id'];
  readonly wasPublishedAsDraft: boolean;
  readonly campaignHashtag?: string;
  readonly isAnswersDefault?: boolean;
  readonly title?: string;
}

export interface ArticleThreadStarterPublisher extends ThreadStarterPublisher {
  readonly articleId?: Group['id'] | User['id'];
  readonly onBehalfOfSenderId?: User['id'];
}

export interface GroupNewPollPublisher extends GroupThreadStarterPublisher {
  readonly postType: PublisherPostType.GroupNewPoll;
  readonly contentType: PublisherContentType.Poll;
  readonly destination: PublisherDestination.Group;
  readonly isEditing: false;
  readonly isReply: false;
}

export interface GroupNewPraisePublisher extends GroupThreadStarterPublisher {
  readonly postType: PublisherPostType.GroupNewPraise;
  readonly contentType: PublisherContentType.Praise;
  readonly destination: PublisherDestination.Group;
  readonly isEditing: false;
  readonly isReply: false;
}

export interface GroupNewQuestionPublisher extends GroupThreadStarterPublisher {
  readonly postType: PublisherPostType.GroupNewQuestion;
  readonly contentType: PublisherContentType.Question;
  readonly destination: PublisherDestination.Group;
  readonly isEditing: false;
  readonly isReply: false;
  readonly hasCollapsedRelatedQuestionsModule?: boolean;
}

export interface GroupNewArticlePublisher extends GroupThreadStarterPublisher {
  readonly postType: PublisherPostType.GroupNewArticle;
  readonly contentType: PublisherContentType.Article;
  readonly destination: PublisherDestination.Group;
  readonly isEditing: false;
  readonly isReply: false;
}

export interface GroupNewUpdatePublisher extends GroupThreadStarterPublisher {
  readonly postType: PublisherPostType.GroupNewUpdate;
  readonly contentType: PublisherContentType.Regular;
  readonly destination: PublisherDestination.Group;
  readonly isEditing: false;
  readonly isReply: false;
}

export interface UserThreadStarterPublisher extends ThreadStarterPublisher {
  readonly userId: User['id'];
  readonly campaignId?: Campaign['id'];
  readonly onBehalfOfSenderId?: User['id'];
  readonly postType:
    | PublisherPostType.UserNewUpdate
    | PublisherPostType.UserNewPoll
    | PublisherPostType.UserNewPraise
    | PublisherPostType.UserNewQuestion
    | PublisherPostType.UserNewArticle
    | PublisherPostType.UserNewMedia;
  readonly destination: PublisherDestination.User;
  readonly isEditing: false;
  readonly isReply: false;
  readonly topicId?: Topic['id'];
  readonly campaignHashtag?: string;
  readonly announcementAudience?: StorylineAnnouncementAudience;
  readonly announcementAudienceIds?: string[];
  readonly announcementNotificationTargets?: StorylineAnnouncementNotificationTarget[];
  readonly wasPublishedAsDraft: boolean;
}

export interface UserNewPollPublisher extends UserThreadStarterPublisher {
  readonly postType: PublisherPostType.UserNewPoll;
  readonly destination: PublisherDestination.User;
  readonly contentType: PublisherContentType.Poll;
  readonly isEditing: false;
  readonly isReply: false;
}

export interface UserNewMediaPublisher extends UserThreadStarterPublisher {
  readonly postType: PublisherPostType.UserNewMedia;
  readonly destination: PublisherDestination.User;
  readonly contentType: PublisherContentType.Regular;
  readonly isEditing: false;
  readonly isReply: false;
}

export interface UserNewPraisePublisher extends UserThreadStarterPublisher {
  readonly postType: PublisherPostType.UserNewPraise;
  readonly destination: PublisherDestination.User;
  readonly contentType: PublisherContentType.Praise;
  readonly isEditing: false;
  readonly isReply: false;
}

export interface UserNewQuestionPublisher extends UserThreadStarterPublisher {
  readonly postType: PublisherPostType.UserNewQuestion;
  readonly destination: PublisherDestination.User;
  readonly isEditing: false;
  readonly isReply: false;
}

export interface UserNewArticlePublisher extends UserThreadStarterPublisher {
  readonly postType: PublisherPostType.UserNewArticle;
  readonly destination: PublisherDestination.User;
  readonly contentType: PublisherContentType.Article;
  readonly isEditing: false;
  readonly isReply: false;
}

export interface UserNewUpdatePublisher extends UserThreadStarterPublisher {
  readonly postType: PublisherPostType.UserNewUpdate;
  readonly destination: PublisherDestination.User;
  readonly contentType: PublisherContentType.Regular;
  readonly isEditing: false;
  readonly isReply: false;
}

export interface UserMomentNewPublisher extends BasePublisher {
  readonly postType: PublisherPostType.UserMomentNew;
  readonly destination: PublisherDestination.UserMoment;
  readonly contentType: PublisherContentType.Regular;
  readonly isEditing: false;
  readonly isReply: false;
  readonly userId: User['id'];
  readonly isGifPickerOpen: false;
  readonly isTopicBarOpen: true;
  readonly isAnnouncement: false;
  readonly sharedThreadId: undefined;
  readonly sharedMessageId: undefined;
  readonly isMandatoryNotification: false;
}

export interface UserMomentReplyPublisher extends BaseReplyPublisher {
  readonly postType: PublisherPostType.UserMomentReply;
  readonly destination: PublisherDestination.UserMoment;
  readonly contentType: PublisherContentType.Regular;
  readonly userId: User['id'];
  readonly isEditing: false;
  readonly isReply: true;
}

export interface GroupReplyPublisher extends BaseReplyPublisher {
  readonly postType: PublisherPostType.GroupReply;
  readonly destination: PublisherDestination.Group;
  readonly contentType: PublisherContentType.Regular;
  readonly isEditing: false;
  readonly isReply: true;
  readonly groupId: Group['id'];
}

export interface UserReplyPublisher extends BaseReplyPublisher {
  readonly postType: PublisherPostType.UserReply;
  readonly destination: PublisherDestination.User;
  readonly contentType: PublisherContentType.Regular;
  readonly isEditing: false;
  readonly isReply: true;
  readonly userId: User['id'];
}

export interface DirectPublisher extends BasePublisher {
  readonly postType: PublisherPostType.DirectNew | PublisherPostType.DirectReply;
  readonly destination: PublisherDestination.Direct;
  readonly contentType: PublisherContentType.Regular;
  readonly isEditing: false;
  readonly isAnnouncement: false;
  readonly isTopicBarOpen: false;
  readonly sharedMessageId?: Message['id'];
}

export interface EditMessagePublisher extends BasePublisher {
  readonly postType: PublisherPostType.Edit;
  readonly destination: PublisherDestination.Group;
  readonly contentType: PublisherContentType.Regular;
  readonly isEditing: true;
  readonly isReply: false;
  readonly messageId: Message['id'];
  readonly originalNotifiedUserIds: User['id'][];
  readonly praisedUserIds: User['id'][];
  readonly badge?: PraiseBadge;
  readonly isTopicBarOpen: false;
  readonly hasRemovedAttachmentsNotDeletedFromSharePoint: boolean;
  readonly onBehalfOfSenderId?: User['id'];
  readonly shouldShowTitle?: boolean;
  readonly isConvertingToQuestion?: boolean;
  readonly removeHeaderImage?: boolean;
  readonly announcementAudience?: StorylineAnnouncementAudience;
  readonly announcementAudienceIds?: string[];
  readonly announcementNotificationTargets?: StorylineAnnouncementNotificationTarget[];
}

export interface NetworkQuestionNewQuestionPublisher extends ThreadStarterPublisher {
  readonly postType: PublisherPostType.NetworkQuestionNewQuestion;
  readonly destination: PublisherDestination.NetworkQuestion;
  readonly contentType: PublisherContentType.Question;
  readonly isEditing: false;
  readonly isReply: false;
  readonly isTopicBarOpen: true;
  readonly hasCollapsedRelatedQuestionsModule: boolean;
}

export interface NetworkQuestionReplyPublisher extends BaseReplyPublisher {
  readonly postType: PublisherPostType.NetworkQuestionReply;
  readonly destination: PublisherDestination.NetworkQuestion;
  readonly contentType: PublisherContentType.Regular;
  readonly isEditing: false;
  readonly isReply: true;
}

export type PollPublisher = GroupNewPollPublisher | UserNewPollPublisher;
export type PraisePublisher = GroupNewPraisePublisher | UserNewPraisePublisher;
export type QuestionPublisher =
  | EventNewQuestionPublisher
  | GroupNewQuestionPublisher
  | UserNewQuestionPublisher
  | NetworkQuestionNewQuestionPublisher;
export type ArticlePublisher = GroupNewArticlePublisher | UserNewArticlePublisher;

export type EventPublisher = EventReplyPublisher | EventThreadStarterPublisher;
export type GroupPublisher = GroupReplyPublisher | GroupThreadStarterPublisher;
export type UserPublisher = UserReplyPublisher | UserThreadStarterPublisher;
export type UserMomentPublisher = UserMomentNewPublisher | UserMomentReplyPublisher;
export type TeamsMeetingPublisher = TeamsMeetingReplyPublisher | TeamsMeetingThreadStarterPublisher;
export type NetworkQuestionPublisher = NetworkQuestionNewQuestionPublisher | NetworkQuestionReplyPublisher;

export type Publisher =
  | BaseReplyPublisher
  | GroupPublisher
  | EventPublisher
  | DirectPublisher
  | TeamsMeetingPublisher
  | EditMessagePublisher
  | UserPublisher
  | UserMomentPublisher
  | NetworkQuestionPublisher;

export const isPollPublisher = (publisher: Publisher): publisher is PollPublisher =>
  publisher.postType === PublisherPostType.GroupNewPoll || publisher.postType === PublisherPostType.UserNewPoll;

export const isArticlePublisher = (publisher: Publisher): publisher is ArticlePublisher =>
  publisher.postType === PublisherPostType.GroupNewArticle || publisher.postType === PublisherPostType.UserNewArticle;

export const isPraisePublisher = (publisher: Publisher): publisher is PraisePublisher =>
  publisher.postType === PublisherPostType.GroupNewPraise || publisher.postType === PublisherPostType.UserNewPraise;

export const isQuestionPublisher = (publisher: Publisher): publisher is QuestionPublisher =>
  publisher.postType === PublisherPostType.EventNewQuestion ||
  publisher.postType === PublisherPostType.UserNewQuestion ||
  publisher.postType === PublisherPostType.GroupNewQuestion ||
  publisher.postType === PublisherPostType.NetworkQuestionNewQuestion;

export const isDirectPublisher = (publisher: Publisher): publisher is DirectPublisher =>
  publisher.destination === PublisherDestination.Direct;

export const isEventReplyPublisher = (publisher: Publisher): publisher is EventReplyPublisher =>
  publisher.postType === PublisherPostType.EventReply;

export const isEventPublisher = (publisher: Publisher): publisher is EventPublisher =>
  isEventThreadStarterPublisher(publisher) || publisher.postType === PublisherPostType.EventReply;

export const isEventThreadStarterPublisher = (publisher: Publisher): publisher is EventThreadStarterPublisher =>
  publisher.postType === PublisherPostType.EventNewQuestion || publisher.postType === PublisherPostType.EventNewUpdate;

export const isGroupPollPublisher = (publisher: Publisher): publisher is GroupNewPollPublisher =>
  publisher.postType === PublisherPostType.GroupNewPoll;

export const isGroupPraisePublisher = (publisher: Publisher): publisher is GroupNewPraisePublisher =>
  publisher.postType === PublisherPostType.GroupNewPraise;

export const isGroupQuestionPublisher = (publisher: Publisher): publisher is GroupNewQuestionPublisher =>
  publisher.postType === PublisherPostType.GroupNewQuestion;

export const isGroupReplyPublisher = (publisher: Publisher): publisher is GroupReplyPublisher =>
  publisher.postType === PublisherPostType.GroupReply;

export const isGroupPublisher = (publisher: Publisher): publisher is GroupPublisher =>
  isGroupThreadStarterPublisher(publisher) || isGroupReplyPublisher(publisher);

export const isGroupThreadStarterPublisher = (publisher: Publisher): publisher is GroupThreadStarterPublisher =>
  publisher.postType === PublisherPostType.GroupNewPoll ||
  publisher.postType === PublisherPostType.GroupNewPraise ||
  publisher.postType === PublisherPostType.GroupNewQuestion ||
  publisher.postType === PublisherPostType.GroupNewArticle ||
  publisher.postType === PublisherPostType.GroupNewUpdate;

export const isTeamsMeetingReplyPublisher = (publisher: Publisher): publisher is TeamsMeetingReplyPublisher =>
  publisher.postType === PublisherPostType.TeamsMeetingReply;

export const isTeamsMeetingPublisher = (publisher: Publisher): publisher is TeamsMeetingPublisher =>
  isTeamsMeetingThreadStarterPublisher(publisher) || isTeamsMeetingReplyPublisher(publisher);

export const isTeamsMeetingThreadStarterPublisher = (
  publisher: Publisher
): publisher is TeamsMeetingThreadStarterPublisher =>
  publisher.postType === PublisherPostType.TeamsMeetingNewQuestion ||
  publisher.postType === PublisherPostType.TeamsMeetingNewUpdate;

export const isUserThreadStarterPublisher = (publisher: Publisher): publisher is UserThreadStarterPublisher =>
  publisher.postType === PublisherPostType.UserNewPoll ||
  publisher.postType === PublisherPostType.UserNewPraise ||
  publisher.postType === PublisherPostType.UserNewQuestion ||
  publisher.postType === PublisherPostType.UserNewArticle ||
  publisher.postType === PublisherPostType.UserNewMedia ||
  publisher.postType === PublisherPostType.UserNewUpdate;

export const isUserMomentThreadStarterPublisher = (publisher: Publisher): publisher is UserMomentNewPublisher =>
  publisher.postType === PublisherPostType.UserMomentNew;

export const isUserMomentReplyPublisher = (publisher: Publisher): publisher is UserMomentReplyPublisher =>
  publisher.postType === PublisherPostType.UserMomentReply;

export const isUserPollPublisher = (publisher: Publisher): publisher is UserNewPollPublisher =>
  publisher.postType === PublisherPostType.UserNewPoll;

export const isUserPraisePublisher = (publisher: Publisher): publisher is UserNewPraisePublisher =>
  publisher.postType === PublisherPostType.UserNewPraise;

export const isUserQuestionPublisher = (publisher: Publisher): publisher is UserNewQuestionPublisher =>
  publisher.postType === PublisherPostType.UserNewQuestion;

export const isUserReplyPublisher = (publisher: Publisher): publisher is UserReplyPublisher =>
  publisher.postType === PublisherPostType.UserReply;

export const isUserPublisher = (publisher: Publisher): publisher is UserPublisher =>
  isUserThreadStarterPublisher(publisher) || isUserReplyPublisher(publisher);

export const isNetworkQuestionNewQuestionPublisher = (
  publisher: Publisher
): publisher is NetworkQuestionNewQuestionPublisher =>
  publisher.postType === PublisherPostType.NetworkQuestionNewQuestion;

export const isNetworkQuestionReplyPublisher = (publisher: Publisher): publisher is NetworkQuestionReplyPublisher =>
  publisher.postType === PublisherPostType.NetworkQuestionReply;

export const isNetworkQuestionPublisher = (publisher: Publisher): publisher is NetworkQuestionPublisher =>
  isNetworkQuestionNewQuestionPublisher(publisher) || isNetworkQuestionReplyPublisher(publisher);

export type ReplyPublisher =
  | BaseReplyPublisher
  | EventReplyPublisher
  | GroupReplyPublisher
  | NetworkQuestionReplyPublisher
  | TeamsMeetingReplyPublisher
  | UserMomentReplyPublisher
  | UserReplyPublisher;
export const isReplyPublisher = (publisher: Publisher): publisher is ReplyPublisher =>
  isGroupReplyPublisher(publisher) ||
  isUserReplyPublisher(publisher) ||
  isUserMomentReplyPublisher(publisher) ||
  isNetworkQuestionReplyPublisher(publisher) ||
  isTeamsMeetingReplyPublisher(publisher) ||
  isEventReplyPublisher(publisher) ||
  publisher.isReply;

export const isEditPublisher = (publisher: Publisher): publisher is EditMessagePublisher => publisher.isEditing;

export type Publishers = Record<Publisher['id'], Publisher>;

export enum MentionsBarState {
  Hidden = 'Hidden',
  ToggledVisible = 'ToggledVisible',
  AlwaysVisible = 'AlwaysVisible',
}

export const maxMomentBodyLength = 200;
